import React, { useEffect, useState } from "react";
import { Mail, Send, Youtube, Instagram, Sparkles, MessageSquare, Clock, Copy, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const CONTACT_EMAIL = "wealthsecret23@gmail.com";

const CHANNELS = [
  { key: "email", label: "Email us", value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}`, Icon: Mail, accent: "linear-gradient(135deg,#38bdf8,#2563eb)", sub: "Replies within 24h" },
  { key: "telegram", label: "Telegram", value: "@wealthsecretofficial1", href: "https://t.me/wealthsecretofficial1", Icon: Send, accent: "linear-gradient(135deg,#229ED9,#2AABEE)", sub: "Live community" },
  { key: "youtube", label: "YouTube", value: "@wealthsecret23", href: "https://www.youtube.com/@wealthsecret23", Icon: Youtube, accent: "linear-gradient(135deg,#ff0000,#c81d25)", sub: "1M+ traders" },
  { key: "instagram", label: "Instagram", value: "@wealthsecretofficial", href: "https://www.instagram.com/wealthsecretofficial/", Icon: Instagram, accent: "linear-gradient(135deg,#f58529,#dd2a7b,#8134af)", sub: "Daily insights" },
];

const inp = "w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(148,163,184,0.2)] rounded-xl px-4 py-3 text-[14px] text-white outline-none focus:border-[color:var(--ws-cyan)] transition-colors placeholder:text-[color:var(--ws-muted)]";

const ContactUs = ({ onGetStarted }) => {
  const [f, setF] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const copyEmail = () => {
    navigator.clipboard?.writeText(CONTACT_EMAIL);
    setCopied(true); toast.success("Email copied");
    setTimeout(() => setCopied(false), 1800);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.trim() || !f.message.trim()) return toast.error("Please add your email and a message.");
    setBusy(true);
    try {
      const r = await fetch(`${API}/support/ticket`, { method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(f) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof j.detail === "string" ? j.detail : "Could not send");
      setSent(true); toast.success("Message sent — we'll reply by email.");
    } catch (er) { toast.error(er.message); }
    setBusy(false);
  };

  return (
    <div className="ws-landing ws-about" data-testid="contact-page">
      <nav className="ws-nav-wrap" data-testid="contact-nav">
        <div className="ws-nav-pill">
          <a href="/" className="ws-nav-brand" data-testid="contact-nav-home">
            <span className="ws-logo-3d w-9 h-9"><img src="/wsj-logo.webp" alt="Wealth Secret Journal" /></span>
            <span className="leading-none">
              <span className="block text-white font-extrabold text-[15px] tracking-tight">Wealth Secret</span>
              <span className="block text-[9px] tracking-[0.28em] text-[color:var(--ws-muted)] uppercase">Journal</span>
            </span>
          </a>
          <div className="ws-nav-links">
            <a href="/" className="ws-nav-link">Home</a>
            <a href="/#features" className="ws-nav-link">Features</a>
            <a href="/about" className="ws-nav-link">About</a>
          </div>
          <div className="ws-nav-actions">
            <button onClick={onGetStarted} className="ws-btn-3d ws-nav-cta" data-testid="contact-getstarted">
              <Sparkles className="w-4 h-4" /> Start Free
            </button>
          </div>
        </div>
      </nav>

      <header className="ws-about-hero" style={{ paddingBottom: 30 }}>
        <div className="ws-about-aurora" aria-hidden />
        <div className="relative z-10 max-w-[720px]">
          <span className="ws-about-eyebrow"><MessageSquare className="w-3.5 h-3.5" /> Contact Us</span>
          <h1 className="ws-about-title">Talk to the <span className="ws-brand-name">Wealth Secret</span> team</h1>
          <p className="ws-about-lead">
            Questions about connecting your broker, VIP access, partnerships or anything else — reach us directly and
            a real human will get back to you.
          </p>
          <a href={`mailto:${CONTACT_EMAIL}`} className="mt-6 inline-flex items-center gap-3 ws-panel px-5 py-3 rounded-2xl hover:border-[color:var(--ws-cyan)] transition-colors group" data-testid="contact-email-hero">
            <span className="ws-icon-chip w-9 h-9"><Mail className="w-4.5 h-4.5" /></span>
            <span className="text-white font-semibold text-[16px] sm:text-[18px] tracking-tight">{CONTACT_EMAIL}</span>
            <button type="button" onClick={(e) => { e.preventDefault(); copyEmail(); }} className="ml-1 text-[color:var(--ws-muted)] hover:text-white" title="Copy email" data-testid="contact-email-copy">
              {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </a>
        </div>
      </header>

      <section className="max-w-[1280px] mx-auto px-6 pb-24 grid lg:grid-cols-[1.1fr_0.9fr] gap-8 relative z-10">
        <form onSubmit={submit} className="ws-panel p-6 sm:p-8" data-testid="contact-form">
          <h2 className="ws-heading text-lg font-bold text-white">Send a message</h2>
          <p className="text-[13px] text-[color:var(--ws-muted)] mt-1 mb-6">Lands straight in our team inbox. We reply to the email you enter.</p>
          {sent ? (
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-6 text-center" data-testid="contact-sent">
              <Check className="w-8 h-8 mx-auto text-emerald-400 mb-2" />
              <div className="text-white font-semibold">Message received</div>
              <p className="text-[13px] text-[color:var(--ws-muted)] mt-1">We'll get back to you at <b className="text-white">{f.email}</b> shortly.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input className={inp} placeholder="Your name" value={f.name} onChange={set("name")} data-testid="contact-name" />
                <input className={inp} type="email" placeholder="Your email" value={f.email} onChange={set("email")} required data-testid="contact-email" />
              </div>
              <textarea className={`${inp} min-h-[160px]`} placeholder="How can we help?" value={f.message} onChange={set("message")} required data-testid="contact-message" />
              <button type="submit" disabled={busy} className="ws-btn-primary w-full sm:w-auto px-6 py-3 rounded-xl text-[14px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="contact-submit">
                {busy ? "Sending…" : <>Send message <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          )}
        </form>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {CHANNELS.map(({ key, label, value, href, Icon, accent, sub }) => (
              <a key={key} href={href} target={key === "email" ? undefined : "_blank"} rel="noopener noreferrer"
                 className="ws-panel ws-panel-hover p-5 flex flex-col gap-3" data-testid={`contact-channel-${key}`}>
                <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: accent }}><Icon className="w-5 h-5" /></span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--ws-muted)]">{label}</div>
                  <div className="text-white font-semibold text-[14px] break-all mt-0.5">{value}</div>
                  <div className="text-[12px] text-[color:var(--ws-muted)] mt-1">{sub}</div>
                </div>
              </a>
            ))}
          </div>
          <div className="ws-panel p-5 flex items-start gap-3" data-testid="contact-hours">
            <span className="ws-icon-chip w-9 h-9 shrink-0"><Clock className="w-4 h-4" /></span>
            <div className="text-[13px] text-[color:var(--ws-muted)] leading-relaxed">
              <b className="text-white">Support hours:</b> Monday – Saturday, 10:00 – 19:00 IST. Logged-in traders can also use the
              in-app support bubble (bottom-right) for instant AI help or to open a ticket.
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[color:var(--ws-border)] py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-[13px]">
          <span className="text-[color:var(--ws-muted)]">© 2026 Wealth Secret. Real MT5 data.</span>
          <nav className="flex flex-wrap gap-x-5 gap-y-2">
            <a href="/about" className="text-[color:var(--ws-muted)] hover:text-white">About</a>
            <a href="/disclaimer" className="text-[color:var(--ws-muted)] hover:text-white">Disclaimer</a>
            <a href="/privacy" className="text-[color:var(--ws-muted)] hover:text-white">Privacy</a>
            <a href="/terms" className="text-[color:var(--ws-muted)] hover:text-white">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default ContactUs;
